import WebStorage from './webStorage';
import { LoginResult } from '../types/api.types';

const AUTH_TOKEN_KEY = 'auth_token';
const USER_DATA_KEY = 'user_data';
const TOKEN_EXPIRY_KEY = 'token_expiry';

export class AuthService {
  static async getToken(): Promise<string | null> {
    const token = await WebStorage.getItem(AUTH_TOKEN_KEY);
    
    if (token) {
      const expiry = await WebStorage.getItem(TOKEN_EXPIRY_KEY);
      if (expiry && new Date().getTime() > parseInt(expiry)) {
        await this.clearAuth();
        return null;
      }
    }
    
    return token;
  }

  static async setAuth(loginResult: LoginResult): Promise<void> {
    const expiryTime = new Date().getTime() + (loginResult.expires_in * 1000);
    
    await WebStorage.multiSet([
      [AUTH_TOKEN_KEY, loginResult.auth_token],
      [USER_DATA_KEY, JSON.stringify(loginResult)],
      [TOKEN_EXPIRY_KEY, expiryTime.toString()]
    ]);
  }

  static async getUserData(): Promise<LoginResult | null> {
    const userData = await WebStorage.getItem(USER_DATA_KEY);
    if (userData) {
      try {
        return JSON.parse(userData);
      } catch (error) {
        console.error('Error parsing user data:', error);
        return null;
      }
    }
    return null;
  }

  static async clearAuth(): Promise<void> {
    await WebStorage.multiRemove([AUTH_TOKEN_KEY, USER_DATA_KEY, TOKEN_EXPIRY_KEY]);
  }

  static async isAuthenticated(): Promise<boolean> {
    const token = await this.getToken();
    return !!token;
  }

  static hasRole(roles: string[], requiredRole: string): boolean {
    return roles.includes(requiredRole);
  }

  static isOperator(roles: string[]): boolean {
    return this.hasRole(roles, 'Operator');
  }

  static async getCurrentUserRoles(): Promise<string[]> {
    const userData = await this.getUserData();
    return userData?.roles || [];
  }

  static parseJwtPayload(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error parsing JWT:', error);
      return null;
    }
  }
}

export default AuthService;